<template>
  <div class="noa-notes-widget">
    <div v-if="loading && !widgetInitialized" class="text-center">
      <div class="loading loading-lg"></div>
      <p class="mt-2">Inicializando Noa Notes...</p>
    </div>
    <div id="noa-widget-container"></div>
  </div>
</template>

<script>
export default {
  props: ['patientId', 'medicalRecordId', 'isEnabled'],
  data() {
    return {
      loading: false,
      sessionData: null,
      widgetInitialized: false,
    };
  },
  async mounted() {
    if (this.isEnabled && this.medicalRecordId) {
      await this.createSession();
      this.initializeWidget();
    }
  },
  watch: {
    medicalRecordId(newId) {
      if (newId && this.isEnabled && !this.sessionData) {
        this.createSession();
      }
    },
  },
  beforeDestroy() {
    const container = document.getElementById('noa-widget-container');
    if (container) {
      container.innerHTML = '';
    }
  },
  methods: {
    async createSession() {
      if (!this.medicalRecordId) {
        return;
      }

      this.loading = true;

      try {
        const response = await this.$http.get(
          `/noa-notes/medical-records/${this.medicalRecordId}/session`,
        );

        this.sessionData = response.data;
      } catch (error) {
        this.$toast.show('Erro ao criar sessão Noa Notes', { type: 'error' });
      } finally {
        this.loading = false;
      }
    },
    initializeWidget() {
      if (!this.sessionData) {
        return;
      }

      if (typeof window.NoaNotesWidgetLauncher === 'undefined') {
        this.$toast.show('Widget Noa Notes não disponível. Recarregue a página.', { type: 'error' });
        return;
      }

      try {
        this.loading = true;
        window.NoaNotesWidgetLauncher.noaWidgetLauncher({
          sessionToken: this.sessionData.sessionToken,
          sessionId: this.sessionData.sessionId,
          consumerId: this.sessionData.consumerId,
          expiresIn: this.sessionData.expiresIn || 3600,
          recorderUrl: this.sessionData.recorderUrl,
          summaryUrl: this.sessionData.summaryUrl,
          locale: 'pt-BR',
          containerSelector: '#noa-widget-container',
          environment: 'sandbox',
          onStatusChange: this.handleStatusChange,
          onRestartAfterFailure: this.handleRestartAfterFailure,
        });

        this.widgetInitialized = true;
        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.$toast.show(
          error.message || 'Erro ao inicializar Noa Notes',
          { type: 'error' },
        );
      }
    },
    async handleStatusChange(status) {
      if (status === 'Verified') {
        await this.fetchSummary();
      } else if (status === 'Failed') {
        this.$toast.show(
          'Erro ao processar áudio no Noa Notes',
          { type: 'error' },
        );
      } else if (status === 'Interrupted') {
        this.$toast.show(
          'Conexão com gravação foi perdida',
          { type: 'warning' },
        );
      }
    },
    async handleRestartAfterFailure() {
      this.widgetInitialized = false;
      await this.createSession();
      if (this.sessionData) {
        this.initializeWidget();
      }
    },
    async fetchSummary() {
      this.loading = true;

      try {
        const response = await this.$http.get(
          `/noa-notes/sessions/${this.sessionData.sessionId}/summary`,
        );

        this.$emit('summarized', response.data);

        this.$toast.success('Resumo do Noa Notes carregado com sucesso!');
      } catch (error) {
        this.$toast.show('Erro ao buscar resumo do atendimento', { type: 'error' });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

.noa-notes-widget {
  .text-center {
    text-align: center;
    padding: $layout-spacing-lg;

    p {
      color: $gray-color-700;
      margin: $layout-spacing 0 0 0;
    }
  }

  #noa-widget-container {
    display: block;
    min-height: 100px;
    --noa-font-size-body: 14px !important;
    --noa-color-foreground-accent: #{$purple-neon-color-500};
    --noa-widget-button-background: #{$purple-neon-color-500};
    --noa-widget-button-primary-background: #{$purple-neon-color-500};
    --noa-widget-button-primary-background-hover: #{$purple-neon-color-600};
    --noa-widget-button-primary-background-outline: #{$purple-neon-color-700};
    --noa-widget-button-background-hover: #{$purple-neon-color-600};
    --noa-widget-button-background-outline: #{$purple-neon-color-700};
    --noa-widget-button-foreground: #ffffff;
    --noa-widget-description-color: #{$gray-color-700};
    --noa-widget-title-color: #{$gray-color-900};
    --noa-color-border-accent: transparent;
    --noa-color-border: transparent;
    --noa-border-width: 0;
    --noa-font-size-display-heading: 1.2rem;
    --noa-border-radius-m: 0px;

    button {
      border-radius: 0px;
    }
  }
}
</style>
